import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { toLanguage } from '@core/i18n/domain/language';
import { I18N_SEED_STATE_KEY, preloadLocalTranslations } from '@core/i18n/data/translations.dictionary';
import { StorageKeys } from '@core/storage/storage.keys';
import { installViewTransitionAbortGuard } from '@core/routing';
import { markClientRenderModeIfNotServerRendered } from '@core/platform/client-render-mode';

/**
 * Suppress a known-external crash: some browser extensions / embedded widgets
 * inject their own minified Web Vitals reporting (the shape of the `web-vitals`
 * library Firebase Performance Monitoring bundles) that calls `reportAllChanges`
 * from a `setTimeout` and throws
 * "Cannot read properties of undefined (reading 'startTime')" when the
 * performance entry it expects isn't available in that context.
 *
 * Nothing in this app reads `PerformanceEntry.startTime` (verified — no
 * `PerformanceObserver` / `getEntriesByType` usage anywhere in `src`, and no
 * `firebase/performance` import), so it can only originate outside our bundle:
 * every reported frame is `<anonymous>` / `VM…`, i.e. injected or `eval`-ed
 * script with no file of its own.
 *
 * The guard is therefore narrow on BOTH axes — the message must be this exact
 * TypeError, AND the frame must not belong to one of our own served scripts.
 * Chrome prefixes `ErrorEvent.message` with "Uncaught TypeError: ", so match the
 * message tail rather than the whole string (an exact-equality check silently
 * matched nothing). The promise-rejection form of the same throw is covered too.
 * Anything else — including this message thrown from our own code — still surfaces.
 */
const EXTERNAL_WEB_VITALS_CRASH = /Cannot read properties of undefined \(reading 'startTime'\)/;

/** True for a frame that is not one of our own served scripts (injected / eval'd). */
function isForeignScript(filename: string | undefined): boolean {
  return !filename || !filename.startsWith(location.origin);
}

window.addEventListener('error', (event) => {
  if (EXTERNAL_WEB_VITALS_CRASH.test(event.message) && isForeignScript(event.filename)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  const stack = reason instanceof Error ? (reason.stack ?? '') : '';
  if (EXTERNAL_WEB_VITALS_CRASH.test(message) && !stack.includes(location.origin)) {
    event.preventDefault();
  }
});

// Tame the benign `AbortError: Transition was skipped` the View Transitions API
// raises for every skipped / superseded route transition — see
// `@core/routing/view-transitions`. Must run before the router creates its first
// transition, so it lives here rather than in a provider.
installViewTransitionAbortGuard();

// Flag a client-rendered page (`ng serve`, no SSR hydration data) with Angular's
// `ngcm` marker, so the shared `provideClientHydration()` does not log a false
// NG0505. Server-rendered pages are untouched and hydrate as before. See
// `@core/platform/client-render-mode`.
markClientRenderModeIfNotServerRendered();

// NOTE: The Firebase Cloud Messaging service worker (/firebase-messaging-sw.js)
// is registered on demand by v2's web-push.service.ts — only when the user
// actually has/uses notifications (has a token, or grants permission), never
// eagerly on every boot for permission-less visitors. Registering it here too
// would be redundant (same script + scope → idempotent) and force-activate the
// SW on every page load. Do not add it here.

/**
 * Read the stored language BEFORE Angular's DI graph exists, so the correct
 * per-language translations chunk (see translations.dictionary.ts) can be
 * preloaded before bootstrap — the same value `TranslationFacade`'s own
 * `readStoredLanguage()` resolves moments later via `StorageService`,
 * duplicated here since that service isn't constructable pre-bootstrap.
 * Any failure (private mode, corrupt value, SSR-less static hosting probe)
 * falls back to the default language rather than blocking boot.
 */
function readStoredLanguageForPreload(serverLang: string | null) {
  try {
    const raw = localStorage.getItem(StorageKeys.LANGUAGE);
    return toLanguage(raw ? (JSON.parse(raw) as string) : serverLang);
  } catch {
    return toLanguage(serverLang);
  }
}

/**
 * Language of the server render's i18n seed (TransferState, written by `TranslationFacade`), or
 * null on a client-only render. Same fallback order as `LanguageService.load()` in the browser:
 * stored choice first, then the server render's language.
 */
function readServerSeedLanguage(): string | null {
  const state = document.getElementById('ng-state')?.textContent ?? '';
  return state.match(new RegExp(`"${I18N_SEED_STATE_KEY}":\\{"lang":"([a-z]+)"`))?.[1] ?? null;
}

// Load the active language's dictionary chunk. On a server-rendered page whose seed matches that
// language, boot right away: the seed holds every string the server render used, so hydration
// shows the same text and TranslationFacade fetches the chunk at idle (mobile LCP). Otherwise (client-only
// render, or a stored language different from the render) wait for the chunk BEFORE bootstrapping,
// as before. A preload failure must never block booting — the facade degrades to humanised keys.
const serverLang = readServerSeedLanguage();
const bootLang = readStoredLanguageForPreload(serverLang);
const boot = () => bootstrapApplication(App, appConfig).catch((err) => console.error(err));
if (serverLang === bootLang) boot();
else void preloadLocalTranslations(bootLang).catch(() => undefined).finally(boot);
